<template>
  <div class="notifications">
    <transition-group
      name="notification-animation"
      class="notifications-list"
      tag="div"
    >
      <div
        class="notifications-list__item"
        v-for="notification in notifications"
        :key="notification.id"
      >
        <message-notification-item
          :notification="notification"
          @close="closeNotification"
        />
      </div>
    </transition-group>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import WebSocketService from '@/api/ws/WebSocketService'
import MessageNotificationItem from './MessageNotificationItem'
import { copyObject } from '../../helpers/helpers'

export default {
  components: {
    MessageNotificationItem
  },

  computed: {
    ...mapGetters(['liveChannel', 'currentNotification', 'authUser'])
  },

  data () {
    return {
      notifications: [],
      timeOutNotification: 5000,
      newMessageSound: new Audio(require('@/assets/sounds/new-message-sound.ogg')),
      interval: null, // interval for title
      oldTitle: document.title
    }
  },

  async created () {
    // Для отключения нотификации в title
    document.body.addEventListener('mouseover', this.disableNewMessageInTitle)

    await this.clearClients()

    await this.fetchLiveToken()

    const service = new WebSocketService()

    service.socket.on('connect', () => {

      service.socket.subscribe(this.liveChannel, (response) => {
        if (response.data.notification_type === 'conversation') {
          this.pushNotification(response.data)

          if (
            response.data.sender_type === 'client'
            && response.data.unread_count > 0
          ) {
            // Включить мигание тайтла
            this.enableNewMessageInTitle()

            // Логика показывания уведомлений
            this.logicOFShowNotifications(response.data)
          }
        } else if (response.data.notification_type === 'unread_messages') {
          this.updateUnreadMessages(response.data.unread_messages)
        }
      })

    })

    service.socket.connect()

    this.checkNotifications()
  },

  methods: {
    ...mapActions(['fetchLiveToken', 'clearClients', 'pushNotification', 'updateUnreadMessages']),

    logicOFShowNotifications (notification) {
      const isAssigned              = notification.is_assigned_by_you
      const isCurrentBrowserTab     = document.visibilityState === 'visible'
      const currentPageIsChat       = this.$route.name === 'chats'
      const notificationsHasEnabled = (
        this.authUser.settings
        && this.authUser.settings['notifications.enabled']
        && Notification.permission === 'granted'
      )

      // Диалог НЕ прикреплен и нотификация ВКЛЮЧЕНА
      if (!isAssigned && notificationsHasEnabled) {
        if (isCurrentBrowserTab) {
          if (!currentPageIsChat) {
            this.showNotification(notification)
          }
        }
      }

      // Диалог прикреплен и нотификация ВКЛЮЧЕНА
      if (isAssigned && notificationsHasEnabled) {
        this.playSoundNotification()

        if (isCurrentBrowserTab) {
          if (!currentPageIsChat) {
            this.showNotification(notification)
          }
        } else {
          this.showNativeNotification(notification)
        }
      }

      // Диалог прикреплен и нотификация ВЫКЛЮЧЕНА
      if (isAssigned && !notificationsHasEnabled) {
        if (isCurrentBrowserTab) {
          this.playSoundNotification()

          if (!currentPageIsChat) {
            this.showNotification(notification)
          }
        }
      }
    },

    showNotification (message) {
      const notification = copyObject(message)
      notification.id = Date.now()

      this.notifications.push(notification)
      this.autoCloseNotification(notification)
    },

    autoCloseNotification (notification) {
      setTimeout(() => {
        this.closeNotification(notification)
      }, this.timeOutNotification)
    },

    closeNotification (notification) {
      const index = this.notifications.findIndex(notify => notification.id === notify.id)

      if (index !== -1) {
        this.notifications.splice(index, 1)
      }
    },

    getMessageContent (message) {
      switch (message.message_type) {
      case 'text':
        return message.body
      case 'image':
        return '(изображение)'
      case 'file':
        return '(файл)'
      case 'location':
        return '(локация)'
      default:
        return '(сообщение)'
      }
    },

    showNativeNotification (message) {
      const params = {}
      const body = this.getMessageContent(message)

      params.body = `${message.name}: ${body}`
      params.icon = 'data:image/svg;base64,PHN2ZyB3aWR0aD0iMTA2IiBoZWlnaHQ9IjEyNCIgdmlld0JveD0iMCAwIDEwNiAxMjQiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEwMy41NTQgNTQuODY0QzEwMy41ODIgNTQuMTc3OCAxMDMuNTk2IDUzLjQ4ODMgMTAzLjU5NiA1Mi43OTU0QzEwMy41OTYgNTIuNzI2NCAxMDMuNTk1IDUyLjY1NzYgMTAzLjU5NSA1Mi41ODg2QzEwMy41OTUgNTIuNTAzOCAxMDMuNTk2IDUyLjQxOSAxMDMuNTk2IDUyLjMzNDFMMTAzLjU5MyA1Mi4zMzQ2QzEwMy4zNDYgMjQuNDkzMyA4MC42OTg3IDIgNTIuNzk3NyAyQzI0Ljc0MyAyIDIgMjQuNzQxOSAyIDUyLjc5NTRDMiA4MC44NDg5IDI0Ljc0MyAxMDMuNTkxIDUyLjc5NzcgMTAzLjU5MUM1NC4yODc0IDEwMy41OTEgNTUuNzYyMyAxMDMuNTI3IDU3LjIxOTYgMTAzLjQwMUM2My43MzI1IDEwMy40MDEgNjEuMjE3NyAxMTAuMDE0IDU5LjI0MzQgMTE1LjIwNUM1Ny42MDc5IDExOS41MDUgNTYuMzQzMSAxMjIuODMgNjAuODg3NyAxMjAuNjE3Qzg1LjQ1OTQgMTA4LjY0NiAxMDIuNjA2IDgzLjgwMzcgMTAzLjU1NCA1NC44NjRaIiBzdHJva2U9IiNGQTZCMDMiIHN0cm9rZS13aWR0aD0iNCIvPjwvc3ZnPg=='

      if (message.profile_image_url) {
        params.image = message.profile_image_url
      }

      const notification = new Notification('Yolker. Новое сообщение', params)

      notification.onclick = () => {
        window.focus()
        this.$router.push({
          name: 'chats',
          params: {
            client_id: message.client_id
          }
        })
      }
    },

    async checkNotifications () {
      if (
        this.authUser.settings['notifications.enabled']
        && Notification.permission !== 'granted'
      ) {
        await Notification.requestPermission()
      }
    },

    playSoundNotification () {
      this.newMessageSound.play()
    },

    enableNewMessageInTitle () {
      this.disableNewMessageInTitle()
      let flag = true
      this.interval = setInterval(() => {
        if (flag) {
          document.title = 'Новое сообщение'
        } else {
          document.title = this.oldTitle
        }
        flag = !flag
      }, 1000)
    },

    disableNewMessageInTitle () {
      if (this.interval) {
        clearInterval(this.interval)
        document.title = this.oldTitle
        this.interval = null
      }
    }
  },

  beforeDestroy () {
    document.body.removeEventListener('mouseover', this.disableNewMessageInTitle)
  }
}
</script>
